<template>
  <div
    class="results-container"
  >
    <div v-if="isShowReport">
      <!-- header -->
      <div
        class="result-header"
      >
        <span
          @click="hideReport"
          class="absolute cursor-pointer left-4 hover:text-primary-100"
        >
          <aha-icon name="arrow-left" />
        </span>
        <p class="text-sm font-bold text-secondary-100">
          {{ $i18nPresenter.t('Presentation Report') }}
        </p>
      </div>
      <!-- panel -->
      <ReportPanel />
      <!-- end panel -->
    </div>

    <aha-accordion v-else>
      <aha-accordion-item :default-expanded="true" :class="{'title-test-v2': isTreatmentShareV2}">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('Report') }}
          </h5>
        </template>
        <template slot="content">
          <!-- Report -->
          <p class="mb-3 text-xs text-base-90" v-if="!isTreatmentShareV2">
            <span v-if="presentation.exportData && presentation.exportData.reportUrl">
              {{ $i18nPresenter.t(`Here is the engagement rate of this presentation.` +
                `Click the button to see the full report.`)
              }}
            </span>
          </p>
          <div
            class="relative"
            v-if="isLoadingReport ||
              (!isLoadingReport
                && presentation.exportData
                && presentation.exportData.reportUrl
                && presentationReport)"
          >
            <div
              v-if="isDisableForViewer"
              class="overlay-disable-section"
            />
            <ReportEngagementRate
              v-if="!isLoadingReport"
              :engagementRate="engagementRate"
              :engagementLevel="engagementLevel"
              :showTooltip="true"
              :showIcon="true"
              class="mb-2"
            />
            <div
              v-else
              class="flex flex-col mb-2"
            >
              <report-engagement-rate-skeleton />
            </div>
            <aha-button
              variant="base"
              :disabled="isLoadingReport"
              @click="viewReport()"
            >
              <aha-icon
                name="multiple-choice-w"
                width="18"
                height="18"
                viewBox="0 0 30 30"
                class="mr-1"
              />
              {{ $i18nPresenter.t('View more') }}
            </aha-button>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item v-if="!isTreatmentShareV2">
        <template slot="title">
          <h5>
            {{ $i18nPresenter.t('Export to Excel') }}
          </h5>
        </template>
        <template slot="content">
          <div class="relative">
            <p class="mb-3 text-xs">
              {{ $i18nPresenter.t('Export questions and audience responses to an Excel file.') }}
            </p>
            <div
              v-if="isDisableForViewer"
              class="overlay-disable-section"
            />
            <div v-if="$store.state.currentUser.hasExport">
              <aha-button
                variant="base"
                :disabled="loadingExportExcel || exportExcelInProgress"
                @click="exportExcel()"
              >
                <aha-spinner v-if="loadingExportExcel || exportExcelInProgress" />
                <font-awesome-icon
                  :icon="['far', 'file-excel']"
                  v-else
                />

                <span v-if="!hasExcelFile">{{ $i18nPresenter.t('Request Excel file...') }}</span>
                <span v-else-if="loadingExportExcel || exportExcelInProgress">
                  {{ $i18nPresenter.t('Generating Excel file...') }}
                </span>
                <span v-else>{{ $i18nPresenter.t('Request updated file...') }}</span>
              </aha-button>
              <p v-if="hasExcelExportError" class="mt-3 text-xs text-red-100">
                {{ $i18nPresenter.t('Something went wrong with the export process. Please try again.') }}
              </p>
            </div>
            <div class="mb-3" v-else>
              <p class="mb-2 text-xs intro">
                {{ $i18nPresenter.t('Please upgrade your account to enable this feature.') }}
              </p>
              <aha-button variant="success" @click="openUpgrade('EXPORT_EXCEL')">
                {{ $i18nPresenter.t('Upgrade now') }}
              </aha-button>
            </div>

            <div class="mt-3" v-if="hasExcelFile && !isGuestUser">
              <p class="mb-2 text-xs">
                {{ $i18nPresenter.t('Last generated:') }}
                {{ $store.state.presentation.exportData.excelSuccessAt | dateTimeAgo(false) }}
              </p>
              <ul class="pl-4 list-disc">
                <li>
                  <a
                    class="mr-2 font-bold text-link--primary"
                    :href="$store.state.presentation.exportData.excelUrl"
                    target="_blank"
                  >{{ $i18nPresenter.t('Download Excel file') }}</a>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </aha-accordion-item>

      <aha-accordion-item v-if="!isTreatmentShareV2">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('Export to PDF / JPG') }}
          </h5>
        </template>

        <template slot="content">
          <div class="relative">
            <p class="mb-3 text-xs">
              {{ $i18nPresenter.t(`Export screenshots of your slides (with audience responses) ` +
                `into a PDF document or as a set of JPG images. ` +
                `This may take a few minutes to complete.`) }}
            </p>
            <div
              v-if="isDisableForViewer"
              class="overlay-disable-section"
            />
            <div v-if="$store.state.currentUser.hasExport">
              <aha-button
                variant="base"
                :disabled="loadingExportPdf || exportPdfInProgress"
                @click="exportPdf()"
              >
                <aha-spinner v-if="loadingExportPdf || exportPdfInProgress" />
                <font-awesome-icon :icon="['far', 'file-pdf']" v-else />

                <span v-if="!hasPdfFile">{{ $i18nPresenter.t('Request screenshots...') }}</span>
                <span v-else-if="exportPdfInProgress">{{ $i18nPresenter.t('Generating screenshots...') }}</span>
                <span v-else>{{ $i18nPresenter.t('Request new screenshots...') }}</span>
              </aha-button>
              <p v-if="hasPdfExportError" class="mt-3 text-xs text-red-100">
                {{ $i18nPresenter.t('Something went wrong with the export process. Please try again.') }}
              </p>
              <div class="mt-3" v-if="hasPdfFile && !isGuestUser">
                <p class="mb-3 text-xs">
                  {{ $i18nPresenter.t('Last generated:') }}
                  {{ $store.state.presentation.exportData.successAt | dateTimeAgo(false) }}
                </p>
                <ul
                  class="pl-4 font-bold list-disc"
                >
                  <li>
                    <a
                      class="cursor-pointer text-link--primary"
                      :href="$store.state.presentation.exportData.pdfUrl"
                      target="_blank"
                    >{{ $i18nPresenter.t('Download PDF file') }}</a>
                  </li>
                  <li>
                    <a
                      class="cursor-pointer text-link--primary"
                      :href="$store.state.presentation.exportData.zipUrl"
                      target="_blank"
                    >{{ $i18nPresenter.t('Download JPG files (as ZIP)') }}</a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="mb-3" v-else>
              <p class="mb-3 text-xs intro">
                {{ $i18nPresenter.t('Please upgrade your account to enable this feature.') }}
              </p>
              <aha-button variant="success" @click="openUpgrade('EXPORT_PDF')">
                {{ $i18nPresenter.t('Upgrade now') }}
              </aha-button>
            </div>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item v-if="!isTreatmentShareV2">
        <template slot="title">
          <h5>{{ $i18nPresenter.t('Reset results') }}</h5>
        </template>
        <template slot="content">
          <div class="relative">
            <div
              v-if="isDisableForViewer"
              class="overlay-disable-section"
            />
            <!-- Clear audience response -->
            <p class="mb-3 text-xs">
              {{ `${$i18nPresenter.t('Delete all')} ` }}
              <u
                v-tooltip="resetResultsTooltip"
                class="cursor-pointer"
              >{{ $i18nPresenter.t('results') }}</u>
              {{ ` ${$i18nPresenter.t('from participants in this presentation.')}` }}
            </p>
            <ResetResultsButton
              v-if="presentation.id"
              gaAction="Result menu"
              class="block mb-3"
              hideTooltip
            />
            <p class="text-xs">
              {{ $i18nPresenter.t(`To keep the current results, ` +
                `you can make a clean duplicate of this presentation.`) }}
            </p>
            <aha-button
              variant="link"
              class="justify-start pl-0 mb-3 text-xs underline text-base-80"
              @click="duplicatePresentation()"
              :disabled="isGuestUser"
            >
              <span
                v-tooltip="{content:isGuestUser?'This feature is disabled in Demo Mode':false}"
              >{{ $i18nPresenter.t('Duplicate this presentation') }}</span>
              <aha-spinner v-if="loadingDuplicate" />
            </aha-button>
          </div>
        </template>
      </aha-accordion-item>
    </aha-accordion>
    <div v-if="isTreatmentShareV2">
      <div>
        <h5 class="flex items-center text-sm font-bold text-secondary-100 p-4 custom-title">
          {{ $i18nPresenter.t('Export') }}
          <span class="relative ml-2">
            <img
              class="icon-audio-pro"
              src="@/assets/images/icon/audio-pro.svg"
              v-if="isPlanFree"
              @click="openUpgradePro('EXPORT_COMBINE')"
            />
            <div
              v-if="ctaUpgradePro"
              class="cta-upgrade tooltip-audio-upgrade-now"
              v-click-outside="clickOutsideUpgrade"
            >
              <span>{{ $i18nPresenter.t("You've just found a Pro feature!") }}
                <br>{{ $i18nPresenter.t("Upgrade to use it.") }}
                <br>
              </span>
              <aha-button class="button-upgrade-now" variant="success" @click="handleModalUpgradeApply">
                {{ $i18nPresenter.t('Upgrade now') }}
              </aha-button>
            </div>
            <div v-if="ctaUpgradePro" class="upgrade-arrow upgrade-arrow_slide"></div>
          </span>
        </h5>

        <div
          class="export-list"
        >
          <ul>
            <li
              @click="exportExcel()"
              class="flex items-center border-base-30
              hover:bg-primary-20 hover:text-primary-90 hover:border-primary-90"
              :class="{'disabled-cursor': isLoadingExport}"
            >
              <img src="@/assets/images/icon/excel-icon.svg" alt="" class="mr-2"> Excel

              <div v-if="loadingExportExcelV2" class="loading flex items-center">
                {{ $i18nPresenter.t("Generating") }}
                <aha-spinner class="ml-2" />
              </div>
              <i
                class="fas fa-download text-primary-90 fa-fw loading"
                v-if="!loadingExportExcelV2 && !exportDoneExcel"
              ></i>
              <i class="fas fa-check fa-fw loading" v-if="exportDoneExcel"></i>
            </li>
            <li
              @click="exportPdf()"
              class="flex items-center border-base-30
              hover:bg-primary-20 hover:text-primary-90 hover:border-primary-90"
              :class="{'disabled-cursor': isLoadingExport}"
            >
              <img src="@/assets/images/icon/pdf-icon.svg" alt="" class="mr-2">
              PDF <span v-if="loadingExportPdfV2" class="loading flex items-center">
                {{ $i18nPresenter.t("Generating") }}
                <aha-spinner class="ml-2" /></span>
              <i
                class="fas fa-download text-primary-90 fa-fw loading"
                v-if="!loadingExportPdfV2 && !exportDonePdf"
              ></i>
              <i class="fas fa-check fa-fw loading" v-if="exportDonePdf"></i>
            </li>
            <li
              @click="exportPdf('image')"
              class="flex items-center border-base-30
              hover:bg-primary-20 hover:text-primary-90 hover:border-primary-90"
              :class="{'disabled-cursor': isLoadingExport}"
            >
              <img src="@/assets/images/icon/image-icon.svg" alt="" class="mr-2"> Images
              <span v-if="loadingExportImage" class="loading flex items-center">
                {{ $i18nPresenter.t("Generating") }}
                <aha-spinner class="ml-2" /></span>
              <i
                class="fas fa-download text-primary-90 fa-fw loading"
                v-if="!loadingExportImage && !exportDoneImage"
              ></i>
              <i class="fas fa-check fa-fw loading" v-if="exportDoneImage"></i>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- <aha-modal
      v-if="errorOnErasingPresentationData !== null"
      title="Error on erasing data"
      @hide="errorOnErasingPresentationData = null"
    >
      <p class="mb-2 text-red-100" v-html="errorOnErasingPresentationData" />
      <p class="mb-4">
        Please try again
      </p>
      <aha-button @click="errorOnErasingPresentationData = null" class="w-20">
        OK
      </aha-button>
    </aha-modal> -->
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import Config from '@/config'
import Utils from '@/util'
import constants from '@/constant'
import EventBus from '@/eventBus'

import ReportEngagementRate from '@/components-v2/presentation-report/ReportEngagementRate.vue'
import ReportEngagementRateSkeleton from '@/components-v2/presentation-report/ReportEngagementRateSkeleton.vue'
import ReportPanel from '@/components-v2/presentation-report/ReportPanel.vue'
import ResetResultsButton from '@/components-v2/common/reset-results/ResetResultsButton.vue'
import mixpanelUtils from '@/utils/mixpanel.utils'
import Socket from '@/socket'

const { eventCategories, workspace } = constants

const FEATURE_TYPE = {
  EXPORT_EXCEL: 'Export to Excel',
  EXPORT_PDF: 'Export to PDF/JPG',
  EXPORT_COMBINE: 'Export',
}

export default {
  components: {
    ReportEngagementRate,
    ReportEngagementRateSkeleton,
    ReportPanel,
    ResetResultsButton,
  },
  props: {
    contentVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      linkDownload: '',
      loading: false,
      loadingClearData: false,
      loadingDuplicate: false,
      loadingExportPdf: false,
      loadingExportExcel: false,
      isShowReport: false,
      ctaUpgradePro: false,
      exportDonePdf: false,
      exportDoneImage: false,
      exportDoneExcel: false,
    }
  },
  computed: {
    ...mapGetters('presentationReport', [
      'engagementRate',
      'presentationReport',
      'engagementLevel',
      'isLoadingReport',
    ]),
    ...mapGetters([
      'isTreatmentShareV2',
    ]),
    ...mapState({
      isGuestUser: (state) => state.currentUser.isGuest,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      loadingExportImage: (state) => state.loadingExportImage,
      loadingExportPdfV2: (state) => state.loadingExportPdfV2,
      loadingExportExcelV2: (state) => state.loadingExportExcelV2,
    }),
    exportPdfInProgress() {
      return this.$store.state.presentation.exportData
       && this.$store.state.presentation.exportData.status === 'processing'
       && this.$store.state.presentation.exportData.requestAt > Date.now() - 30 * 60000
    },
    hasPdfFile() {
      return this.$store.state.presentation.exportData &&
       !!this.$store.state.presentation.exportData.pdfUrl
    },
    hasPdfExportError() {
      return this.$store.state.presentation.exportData && (
        this.$store.state.presentation.exportData.status === 'failure' ||
        (this.$store.state.presentation.exportData.status === 'processing' &&
         this.$store.state.presentation.exportData.requestAt < Date.now() - 30 * 60000)
      )
    },
    exportExcelInProgress() {
      return this.$store.state.presentation.exportData
       && this.$store.state.presentation.exportData.excelStatus === 'processing'
       && this.$store.state.presentation.exportData.excelRequestAt > Date.now() - 30 * 60000
    },
    hasExcelFile() {
      return this.$store.state.presentation.exportData &&
       !!this.$store.state.presentation.exportData.excelUrl
    },
    hasExcelExportError() {
      return this.$store.state.presentation.exportData && (
        this.$store.state.presentation.exportData.excelStatus === 'failure' ||
        (this.$store.state.presentation.exportData.excelStatus === 'processing' &&
         this.$store.state.presentation.exportData.excelRequestAt < Date.now() - 5 * 60000)
      )
    },
    resetResultsTooltip() {
      return this.$i18nPresenter.t('<p>Results include all participants’ <b> votes</b>, <b>submissions</b> and <b>reactions</b>.</p>')
    },
    isDisableForViewer() {
      if (this.$store.state.currentUser
        && this.$store.state.currentUser.accountRole
        && this.$store.state.currentUser.accountRole === workspace.VIEWER
        && this.$store.state.workingMode === workspace.TEAM
      ) return true
      return false
    },
    isNotFreeUser() {
      return this.currentUser.plan !== 'free'
    },
    isPlanFree() {
      return !this.$store.state.plan || this.$store.state.plan.group === 'free'
    },
    isLoadingExport() {
      return this.loadingExportExcelV2 || this.loadingExportImage || this.loadingExportPdfV2
    },
  },
  watch: {
    isLoadingReport(val) {
      const shouldOpenExport = Utils.getParamValue('report')
      if (shouldOpenExport && !val) {
        this.isShowReport = true
        this.$emit('handle-hide-main-header')
        this.openReportPanelAction()
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Editor Header > Open results menu')
      }
      // if (!val) {
      //   this.isShowReport = true
      // }
    },
  },
  mounted() {
    if (this.isNotFreeUser) {
      this.updateCurrentUserAction({
        hasExport: true,
      })
    }
    this.selectPresentationAction({
      presentation: this.presentation,
    })

    EventBus.$on('export-pdf:success', () => {
      this.setLoadingExportPdfAction(false)
      this.setLoadingExportImageAction(false)
      this.exportDonePdf = true
    })

    EventBus.$on('export-image:success', () => {
      this.$store.state.loadingExportImage = false
      this.setLoadingExportImageAction(false)
      this.setLoadingExportPdfAction(false)
      this.exportDoneImage = true
    })

    EventBus.$on('export-excel:success', () => {
      this.setLoadingExportExcelAction(false)
      this.exportDoneExcel = true
    })

    if (this.exportPdfInProgress) {
      this.setLoadingExportPdfAction(true)
      this.setLoadingExportImageAction(true)
    }
    if (this.exportExcelInProgress) {
      this.setLoadingExportExcelAction(true)
    }
  },
  destroyed() {
    this.clearPresentationReportAction()
    Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_EDITOR, 'Results menu > Report > Close results menu & hide report')
    EventBus.$off('export-data:success')
    EventBus.$off('export-excel:success')
  },
  methods: {
    ...mapActions('presentationReport', [
      'selectPresentationAction',
      'openReportPanelAction',
      'clearPresentationReportAction',
      'generateReportAction',
      'subscribeReportAction',
    ]),
    ...mapActions(['updateCurrentUserAction', 'setLoadingExportPdfAction', 'setLoadingExportImageAction', 'setLoadingExportExcelAction']),
    exportPdf(exportType) {
      if (this.isPlanFree) {
        this.openUpgradePro('EXPORT_COMBINE')
        return
      }
      const isDisable = this.isTreatmentShareV2 && (this.loadingExportExcelV2 || this.loadingExportImage ||
            this.loadingExportPdfV2)
      if (isDisable) return
      if (!exportType && (this.loadingExportPdf || this.loadingExportPdfV2)) return
      if (exportType === 'image' && this.loadingExportImage) return
      if (exportType && exportType === 'image') {
        this.setLoadingExportImageAction(true)
        this.$store.state.exportingType = 'image'
        this.exportDoneImage = false
      } else {
        this.loadingExportPdf = true
        if (this.isTreatmentShareV2) {
          this.setLoadingExportPdfAction(true)
          this.$store.state.exportingType = 'pdf'
          this.exportDonePdf = false
        }
      }

      this.$http.post(Config.workerApi.exportPdf, {
        id: this.$store.state.presentation.id,
      }).then(() => {
        const exportData = { ...this.$store.state.presentation.exportData, status: 'processing', requestAt: Date.now() }

        if (this.isTreatmentShareV2) {
          this.$store.dispatch('onlyPatchPresentationAction', { exportData })
        } else {
          this.$store.dispatch('updatePresentation', { exportData })
        }

        this.loadingExportPdf = false
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Request Export PDF > Success')
      },
      (err) => {
        const exportData = { ...this.$store.state.presentation.exportData, status: 'failure', failureAt: Date.now() }

        if (this.isTreatmentShareV2) {
          this.$store.dispatch('onlyPatchPresentationAction', { exportData })
        } else {
          this.$store.dispatch('updatePresentation', { exportData })
        }

        this.$store.state.loadingExportPdf = false
        this.setLoadingExportImageAction(false)
        this.setLoadingExportPdfAction(false)
        this.$store.state.exportingType = null
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Request Export PDF > Failed', JSON.stringify(err))
      })
    },

    async duplicatePresentation() {
      if (this.loadingDuplicate) return
      this.loadingDuplicate = true
      const isOwner = this.presentation.userId === this.currentUser.id
      let newPresentationId

      try {
        if (isOwner) {
          const duplicateResponse = await this.$http.post(Config.api.duplicatePresentation, {
            name: this.presentation.name,
            id: this.presentation.id,
          })
          newPresentationId = duplicateResponse.body.id
        } else {
          const copyResponse = await this.$http.post(Config.api.copyPresentation, {
            id: this.presentation.shareCode,
            name: this.presentation.name,
            fromWorkspace: !!((this.currentUser.accountId && this.presentation.accountId)),
          })
          newPresentationId = copyResponse.body.id
        }
      } catch (e) {
        Utils.showToastError(this.$i18nPresenter.t('Duplicate this presentation unsuccessfully!'))
        return
      }

      this.loadingDuplicate = true
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Create new presentation', 'Results menu > Duplicate presentation', this.presentation.id, {
        sourcePresentationId: this.presentation.id,
        ...mixpanelUtils.getPresentationContext(this.presentation),
      })
      window.location.href = `${window.location.origin}/presentation/${newPresentationId}`
    },
    async clearData() {
      if (this.loadingClearData) return

      this.loadingClearData = true

      const {
        id: presentationId, uniqueAccessCode, teamPlay,
      }
        = this.presentation

      const payload = {
        id: presentationId,
        socketId: Socket.getSocketId(),
      }
      if (teamPlay) {
        payload.uniqueAccessCode = uniqueAccessCode
        payload.teamPlay = teamPlay
      }

      try {
        await this.$http.post(Config.api.clearAudienceData, payload)
      } catch (error) {
        const errorMsg = JSON.stringify(error)
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Clear Audience data > Failed', errorMsg)

        throw error
      } finally {
        this.loadingClearData = false
      }

      this.clearPresentationReportAction()
      this.$store.dispatch('updatePresentation', {
        exportData: {
          ...this.presentation,
          reportUrl: null,
          reportStatus: null,
          reportUpdatedAt: null,
        },
      })

      this.$store.state.presentation.slides.forEach((slide) => {
        if (slide.type === 'openEnded' || slide.type === 'wordCloud' || slide.type === 'ideas') {
          slide.SlideOptions = []
        }
        if (slide.type === 'spinnerWheel') {
          EventBus.$emit('clearSpinnerWheel', slide.id)
          slide.metadata = {
            ...slide.metadata,
            winner: null,
            entries: null,
            entryUrl: null,
            wheelStatus: null,
          }
        }

        if (slide.type === 'multipleChoice' || slide.type === 'pickAnswer' || slide.type === 'imageChoice') {
          slide.SlideOptions.forEach((slideOption) => {
            slideOption.count = 0
          })
        }
        slide.multipleCountAnswer = 0
        slide.votingStep = constants.IdeasTabName.SUBMISSION
      })

      this.$store.state.audiences = {}
      this.$store.state.countPlayer = 0

      const index = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === this.$store.state.slideActive.id
      })

      this.$store.dispatch('updateSlideActive', { id: null, activeTab: null })
      setTimeout(() => {
        this.$store.dispatch(
          'setSlideActive',
          JSON.parse(JSON.stringify(this.$store.state.presentation.slides[index])),
        )
      }, 0)
      this.$store.state.questions = []
      this.$store.state.audiences = {}
      this.$store.state.tabQnaQuestion = 1
      this.$store.state.lastQuestion = {}
      this.$store.state.questionCount = 0
      this.$store.state.presentation.showExport = false
      this.$store.state.countTopQuestion = 0
      this.$store.state.countPinnedQuestion = 0
      this.$store.state.countAnswerQuestion = 0
      this.$store.state.presentation.numberOfHearts = 0
      this.$store.state.presentation.numberOfLaughs = 0
      this.$store.state.presentation.numberOfLikes = 0
      this.$store.state.presentation.numberOfSads = 0
      this.$store.state.presentation.numberOfWows = 0

      Utils.showToastSuccess(this.$i18nPresenter.t('Presentation data erased.'))
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Clear Audience data > Success')

      if (this.$store.state.presentation.teamData) {
        this.$store.state.presentation.teamData.forEach((team) => {
          team.teams = []
        })
      }
    },

    hideSetting() {
      this.$store.state.presentation.showExport = false
      document.body.classList.remove('no-scroll')
    },

    exportExcel() {
      if (this.isPlanFree) {
        this.openUpgradePro('EXPORT_COMBINE')
        return
      }
      const isDisable = this.isTreatmentShareV2 && (this.loadingExportExcelV2 || this.loadingExportImage ||
            this.loadingExportPdfV2 || this.exportPdfInProgress || this.exportExcelInProgress)
      if (isDisable) return
      if (this.loadingExportPdf) return
      if (this.loadingExportExcel || this.exportExcelInProgress || this.loadingExportExcelV2) return
      this.loadingExportExcel = true
      if (this.isTreatmentShareV2) {
        this.setLoadingExportExcelAction(true)
        this.$store.state.exportingType = 'excel'
        this.exportDoneExcel = false
      }

      this.$http.post(Config.workerApi.exportExcel, {
        id: this.$store.state.presentation.id,
      }).then(() => {
        const exportData = {
          ...this.$store.state.presentation.exportData,
          excelStatus: 'processing',
          excelRequestAt: Date.now(),
        }
        if (this.isTreatmentShareV2) {
          this.$store.dispatch('onlyPatchPresentationAction', { exportData })
        } else {
          this.$store.dispatch('updatePresentation', { exportData })
        }
        this.loadingExportExcel = false
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Export Excel > Success')
      },
      (err) => {
        const exportData = { ...this.$store.state.presentation.exportData, excelStatus: 'failure' }

        if (this.isTreatmentShareV2) {
          this.$store.dispatch('onlyPatchPresentationAction', { exportData })
        } else {
          this.$store.dispatch('updatePresentation', { exportData })
        }

        this.loadingExportExcel = false
        this.setLoadingExportExcelAction(false)
        this.$store.state.exportingType = null
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Export Excel > Failed', `Request export excel fail ${JSON.stringify(err)}`)
      })
    },
    openUpgrade(featureType = FEATURE_TYPE.EXPORT_EXCEL) {
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Feature upgrade',
        `${FEATURE_TYPE[featureType]}`,
      )

      // add redirect to handle case: guest clicks upgrade -> go to sign-up first
      this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },
    generateReport() {
      this.generateReportAction(this.presentation)
      this.subscribeReportAction(this.presentation)

      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'View report', this.presentation.id)
    },
    viewReport() {
      this.isShowReport = !this.isShowReport
      this.$emit('handle-hide-main-header')
      this.openReportPanelAction()
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Report > Click view more')
    },
    hideReport() {
      this.isShowReport = !this.isShowReport
      this.$emit('handle-hide-main-header')
      Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_EDITOR, 'Results menu > Report > Hide report')
    },
    openUpgradePro(featureType = FEATURE_TYPE.EXPORT_COMBINE) {
      this.ctaUpgradePro = true

      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Feature upgrade',
        `${FEATURE_TYPE[featureType]}`,
      )
    },
    handleModalUpgradeApply() {
      this.ctaUpgradePro = false
      this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },

    clickOutsideUpgrade() {
      this.ctaUpgradePro = false
    },
    async downloadFile(link) {
      const response = await this.$http.get(link, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.body]))
      const a = document.createElement('a')
      a.href = url
      a.download = this.getFileNameFromURL(link)
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    },
    getFileNameFromURL(url) {
      // Tách URL bằng dấu '/'
      const parts = url.split('/')

      // Lấy phần tử cuối cùng trong mảng parts
      const lastPart = parts[parts.length - 1]

      // Tách tên tệp từ phần tử cuối cùng bằng dấu '?'
      return lastPart.split('?')[0]
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-section {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full z-20;
}
.result-header {
  @apply sticky top-0 z-20 flex items-center justify-center h-16;
  @apply p-4 shadow-top bg-base-0 border-base-30 text-base-90;
}

.cta-upgrade {
  @apply bg-secondary-100 absolute w-60 p-3 rounded z-50;
  left: -10px;
  top: 1.5rem;
  font-family: 'Nunito', sans-serif;
  padding-bottom: 16px;

  div,
  span {
    @apply text-base-0 text-sm;
  }

  div {
    @apply font-bold;
  }

  span {
    @apply font-normal mt-1;
  }
}

.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, var(--tw-bg-opacity)) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 1.1rem;

  &.upgrade-arrow_presentation {
    left: calc(50% - 47px);
  }

  &.upgrade-arrow_slide {
    left: calc(50% - 8px);
  }
}

.tooltip-audio-upgrade-now {
  .button-upgrade-now {
    margin-top: 12px;
  }
}

h5.custom-title {
  border-left: 4px solid transparent;
}

.export-list {
  ul {
    padding: 0 20px;

    li {
      border-width: 1px;
      position: relative;
      padding: 12px;
      border-radius: 4px;
      margin-bottom: 16px;
      cursor: pointer;
      color: #313131;

      .loading {
        position: absolute;
        right: 16px;
        margin: 0 auto;
        top: 20px;
        width: auto;
      }
      .fa-download {
        display: none;
      }
      &:hover {
        .fa-download {
          display: block;
        }
      }
    }
  }

  .disabled-cursor {
    cursor: not-allowed;
    background-color: rgba(255, 255, 255, var(--tw-bg-opacity));;
    @apply border-base-30;
    &:hover {
      .fa-download {
        display: none;
      }
    }
  }

  &.disabled {
    ul {
      li {
        cursor: not-allowed;
        @apply bg-base-10 text-base-70;
        i {
          @apply text-base-70;
        }
      }
    }
  }
}

.title-test-v2 {
  &::v-deep {
    .accordion-item-title {
      border-color: transparent;
      svg {
        display: none;
      }
    }
    .accordion-item-content {
      padding-bottom: 8px;
    }
  }
}
</style>
